import {Observable} from 'rxjs';

import {effect, Injector, isDevMode, isSignal, runInInjectionContext, signal, Signal, WritableSignal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {MainInjector} from '@shared-types';

export type IWritableSignalExtOptions<T, D = any> = SignalFilter<T> & {
	data?: D;
	/** The injector to use. */
	injector?: Injector;
	/** The number of values to keep in the buffer. */
	bufferSize?: number;
};

export declare type SignalBuffer<T> = {
	/** Returns the buffer of values. */
	getBuffer(): T[];
	/** Returns the buffer size. */
	getBufferSize(): number;
	/** Clears the buffer. */
	resetBuffer(): void;
};

export declare type SignalChangeListner<T> = {
	/**
	 * // for example in service:
	 * public numSignal$ = signalExt<number>(0);
	 *
	 *
	 * // place 1:
	 * numSignal$.onChange((signal) => {});
	 *
	 * // place 2:
	 * numSignal$.onChange((signal) => {});
	 *
	 *
	 * this function helps to listen for changes in the signal in different places.
	 */
	onChange: (value: T) => void;
};

export declare type SignalFilter<T, O extends T = T> = {
	/** inline change function */
	filterFn?: (x: T) => boolean;
};

export declare interface WritableSignalExt<T, D = any> extends Signal<T>, WritableSignal<T>, Observable<T>, SignalBuffer<T>, SignalChangeListner<T> {
	readonly IS_SIGNAL_EXT: true;
	options: IWritableSignalExtOptions<T, D>;

	/** The observable representation of the signal. */
	readonly $: Observable<T>;
	/** Resets the signal to its initial value. */
	reset(): void;

	/** Patches the signal with the provided patches. */
	patch(patches: Partial<T>): ThisType<WritableSignalExt<T>>;
}

export function signalExt<T, D = any>(source: T | Signal<T>, options: Partial<IWritableSignalExtOptions<T, D>> = {}): WritableSignalExt<T> {
	if (source && source['IS_SIGNAL_EXT']) {
		source['options'] = {...source['options'], ...options};
		return source as WritableSignalExt<T, D>;
	}
	const injector = options?.injector || MainInjector || Injector.create({providers: []});
	return runInInjectionContext(injector, () => {
		// set the initial value and implement the reset method:
		const [signalFn, initialValue] = extractSignalFn(source);
		if (signalFn.options) signalFn.options = {...signalFn.options, ...options};
		signalFn.reset = () => signalFn.set(initialValue);

		// initialize the filter	function, if provided:
		if (options.filterFn) initFilter(signalFn);

		// initialize the change listener,	if provided:
		initChangeListner(signalFn);

		// imlement the patch method:
		signalFn.patch = (patches: Partial<T>) => {
			signalFn.set({...signalFn(), ...patches});
			return signalFn;
		};
		/* -----------------------------------------------  Optionals ----------------------------------------------- */
		if (options?.bufferSize > 0) initBuffer(signalFn, options.bufferSize);
		/* -------------------------------------------------------------------------------------------------------------- */
		const obs$ = toObservable(signalFn, {injector: injector});
		return Object.assign(signalFn, {
			IS_SIGNAL_EXT: true,
			options: options,
			$: obs$,
		});
	});

	function extractSignalFn(source: T | Signal<T>): [WritableSignalExt<T>, T] {
		return isSignal(source) ? [source as WritableSignalExt<T>, source()] : [signal(source) as WritableSignalExt<T>, source];
	}

	function initFilter(signalFn: WritableSignalExt<T>): void {
		if (isDevMode()) {
			// TODO: DC-2209 https://app.clickup.com/t/36849242/DC-2209
			debugger;
		}
		const filterFn = options?.filterFn;
		const setFn = signalFn.set;
		signalFn.set = (x: T) => {
			if (filterFn(x)) {
				setFn.call(signalFn, x);
			}
		};
		signalFn.update = (signalUpdateFn: (x: T) => T) => {
			const value = signalUpdateFn(signalFn());
			if (filterFn(value)) {
				setFn.call(signalFn, value);
			}
		};
	}

	function initChangeListner(signalFn: WritableSignalExt<T>): void {
		let prevValue: T = signalFn();
		effect(() => {
			const value = signalFn();
			if (value !== undefined && signalFn?.onChange && value !== prevValue) {
				prevValue = value;
				signalFn?.onChange(value);
			}
		});
	}

	function initBuffer(signalFn: WritableSignalExt<T>, bufferSize: number): void {
		const buffer = Stack<T>(bufferSize);
		signalFn.getBuffer = () => buffer.toArray();
		signalFn.getBufferSize = () => buffer.length;
		effect(() => {
			const value = signalFn();
			if (value !== undefined) {
				buffer.push(value);
			}
		});
	}
}
